<template>
  <b-modal size="xl" v-model="show" :title="modalTitle" no-close-on-backdrop>
    <div v-if="loading" class="text-center py-4">
      <b-spinner small></b-spinner> {{ $t('loading') }}
    </div>
    <div v-else>
      <!-- Basic info -->
      <fieldset class="kyc-section mb-3">
        <legend class="kyc-legend"><i class="fa fa-info-circle mr-1"></i>{{ $t('basicInfo') }}</legend>
        <div class="row">
          <div class="col-md-4">
            <b-form-group :label="$t('code') + ' *'" :description="$t('codeHelp')">
              <b-form-input v-model="form.code" :disabled="isEdit" placeholder="enterprise-standard" />
            </b-form-group>
          </div>
          <div class="col-md-5">
            <b-form-group :label="$t('name') + ' *'">
              <b-form-input v-model="form.name" :placeholder="$t('namePlaceholder')" />
            </b-form-group>
          </div>
          <div class="col-md-3">
            <b-form-group :label="$t('type') + ' *'">
              <b-form-select v-model="form.type" :options="typeOptions" />
            </b-form-group>
          </div>
          <div class="col-md-12">
            <b-form-group :label="$t('description')">
              <b-form-textarea v-model="form.description" rows="2" :placeholder="$t('descriptionPlaceholder')" />
            </b-form-group>
          </div>
        </div>
      </fieldset>

      <!-- File upload -->
      <fieldset class="kyc-section mb-3">
        <legend class="kyc-legend"><i class="fa fa-file-word mr-1"></i>{{ $t('file') }}</legend>
        <div class="row">
          <div class="col-md-8">
            <b-form-file v-model="form.file" accept=".docx" :placeholder="$t('chooseFile')"
              :drop-placeholder="$t('dropFile')" @input="onFileChange" />
            <div class="small text-muted mt-1">
              {{ $t('fileNote') }} <code>{{ braceOpen }}ten_bien{{ braceClose }}</code>
            </div>
            <div v-if="isEdit && form.fileName" class="small text-info mt-1">
              <i class="fa fa-paperclip"></i> {{ $t('currentFile') }}: <b>{{ form.fileName }}</b>
              <span v-if="form.fileSize" class="text-muted"> — {{ formatSize(form.fileSize) }}</span>
              <span class="text-muted ml-2">({{ $t('uploadNewToReplace') }})</span>
            </div>
          </div>
          <div class="col-md-4">
            <button class="btn btn-sm btn-outline-info is-border-1" :disabled="!form.file || extracting"
              @click="extractPlaceholders">
              <i class="fa fa-search"></i>
              {{ extracting ? $t('extracting') : $t('extractPlaceholders') }}
            </button>
          </div>
        </div>
        <div v-if="extractedPlaceholders.length" class="mt-2 alert alert-light">
          <div class="small mb-1"><b>{{ $t('placeholdersFound', [extractedPlaceholders.length]) }}:</b></div>
          <code v-for="p in extractedPlaceholders" :key="p" class="mr-2 mb-1 d-inline-block">{{ braceOpen + p + braceClose }}</code>
          <div v-if="missingVars.length" class="small text-warning mt-1">
            <i class="fa fa-exclamation-triangle"></i>
            {{ $t('missingVars') }}: <code v-for="k in missingVars" :key="k">{{ k }}</code>
          </div>
          <div v-if="extraVars.length" class="small text-warning mt-1">
            <i class="fa fa-exclamation-triangle"></i>
            {{ $t('extraVars') }}: <code v-for="k in extraVars" :key="k">{{ k }}</code>
          </div>
        </div>
      </fieldset>

      <!-- Variables editor -->
      <fieldset class="kyc-section mb-3">
        <legend class="kyc-legend">
          <i class="fa fa-list mr-1"></i>{{ $t('variables') }}
          <button class="btn btn-sm btn-outline-primary ml-2 is-border-1" @click="addVariable">
            <i class="fa fa-plus"></i> {{ $t('addVariable') }}
          </button>
          <button class="btn btn-sm btn-outline-secondary ml-1 is-border-1" @click="cloneFromTemplate"
            :title="$t('cloneFromTemplateHelp')">
            <i class="fa fa-copy"></i> {{ $t('cloneFromTemplate') }}
          </button>
        </legend>

        <div v-if="!form.variables.length" class="text-muted text-center py-3">
          {{ $t('noVariables') }}
        </div>
        <table v-else class="table table-sm table-bordered">
          <thead>
            <tr>
              <th style="width: 18%">{{ $t('varKey') }}</th>
              <th style="width: 22%">{{ $t('varLabel') }}</th>
              <th style="width: 25%">{{ $t('varSource') }}</th>
              <th style="width: 10%">{{ $t('varType') }}</th>
              <th style="width: 8%" class="text-center">{{ $t('varRequired') }}</th>
              <th style="width: 8%" class="text-center">{{ $t('varHidden') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(v, idx) in form.variables" :key="idx">
              <td><b-form-input size="sm" v-model="v.key" placeholder="ten_cong_ty" /></td>
              <td><b-form-input size="sm" v-model="v.label" :placeholder="$t('varLabelPlaceholder')" /></td>
              <td>
                <b-form-input size="sm" v-model="v.source" placeholder="kyc.companyName" :list="'src-list-' + idx" />
                <datalist :id="'src-list-' + idx">
                  <option v-for="s in sourceSuggestions" :key="s" :value="s"></option>
                </datalist>
              </td>
              <td>
                <b-form-select size="sm" v-model="v.type" :options="[
                  {value: 'text', text: 'text'},
                  {value: 'date', text: 'date'},
                ]" />
              </td>
              <td class="text-center"><b-form-checkbox v-model="v.required" /></td>
              <td class="text-center"><b-form-checkbox v-model="v.hidden" /></td>
              <td class="text-right">
                <button class="btn btn-sm btn-link text-danger" @click="form.variables.splice(idx, 1)" :title="$t('remove')">
                  <i class="fa fa-times"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </fieldset>
    </div>

    <template #modal-footer>
      <button class="btn btn-secondary" @click="show = false" :disabled="saving">{{ $t('cancel') }}</button>
      <button class="btn btn-primary" @click="save" :disabled="saving || !canSave">
        <b-spinner small v-if="saving"></b-spinner>
        {{ saving ? $t('saving') : $t('save') }}
      </button>
    </template>
  </b-modal>
</template>

<script>
const EMPTY = {
  _id: null,
  code: '',
  name: '',
  description: '',
  type: 'enterprise',
  fileName: '',
  fileSize: 0,
  file: null,
  variables: [],
  isActive: true,
};

export default {
  data() {
    return {
      service: '/kyc-contract-template',
      braceOpen: '{{',
      braceClose: '}}',
      show: false,
      loading: false,
      saving: false,
      extracting: false,
      form: JSON.parse(JSON.stringify(EMPTY)),
      isEdit: false,
      extractedPlaceholders: [],
      typeOptions: [
        {value: 'enterprise', text: this.$t ? this.$t('enterprise') : 'Doanh nghiệp'},
        {value: 'personal', text: this.$t ? this.$t('personal') : 'Cá nhân'},
        {value: 'all', text: this.$t ? this.$t('all') : 'Cả hai'},
      ],
      sourceSuggestions: [
        'manual',
        'system.today', 'system.day', 'system.month', 'system.year', 'system.exporter', 'system.exportTime',
        'kyc.name', 'kyc.companyName', 'kyc.companyTaxCode', 'kyc.companyAddress',
        'kyc.address', 'kyc.taxCode', 'kyc.shortName', 'kyc.position', 'kyc.contractNumber',
        'client.domain', 'client.phone', 'client.email', 'client.companyName', 'client.contactName',
      ],
    };
  },
  computed: {
    modalTitle() {
      return this.isEdit ? this.$t('editTitle') : this.$t('createTitle');
    },
    canSave() {
      return !!(this.form.code && this.form.name && this.form.type) && (this.isEdit || this.form.file);
    },
    declaredKeys() {
      return (this.form.variables || []).map(v => (v.key || '').trim()).filter(Boolean);
    },
    missingVars() {
      // placeholders trong file nhưng không có trong variables config
      return this.extractedPlaceholders.filter(p => !this.declaredKeys.includes(p));
    },
    extraVars() {
      // variables config nhưng không có placeholder trong file
      if (!this.extractedPlaceholders.length) return [];
      return this.declaredKeys.filter(k => !this.extractedPlaceholders.includes(k));
    },
  },
  methods: {
    formatSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    },
    create() {
      this.form = JSON.parse(JSON.stringify(EMPTY));
      this.isEdit = false;
      this.extractedPlaceholders = [];
      this.show = true;
    },
    edit(item) {
      this.isEdit = true;
      this.extractedPlaceholders = [];
      this.loading = true;
      this.show = true;
      this.$bgService.get(this.service + '/' + item._id)
        .then(res => {
          const d = res.data;
          this.form = {
            _id: d._id,
            code: d.code,
            name: d.name,
            description: d.description || '',
            type: d.type,
            fileName: d.fileName,
            fileSize: d.fileSize || 0,
            file: null,
            variables: JSON.parse(JSON.stringify(d.variables || [])),
            isActive: d.isActive !== false,
          };
        })
        .catch(err => {
          this.$swal({title: this.$t('error'), text: (err.response && err.response.data && err.response.data.message) || err.message, icon: 'error'});
          this.show = false;
        })
        .finally(() => { this.loading = false; });
    },
    onFileChange() {
      this.extractedPlaceholders = [];
    },
    extractPlaceholders() {
      if (!this.form.file) return;
      this.extracting = true;
      const fd = new FormData();
      fd.append('file', this.form.file);
      this.$service.post(this.service + '/extract-placeholders', fd)
        .then(res => { this.extractedPlaceholders = res.data.placeholders || []; })
        .catch(err => {
          this.$swal({title: this.$t('error'), text: (err.response && err.response.data && err.response.data.message) || err.message, icon: 'error'});
        })
        .finally(() => { this.extracting = false; });
    },
    addVariable() {
      this.form.variables.push({
        key: '', label: '', source: 'manual', type: 'text', required: false, hidden: false, description: '',
      });
    },
    cloneFromTemplate() {
      // Lấy template đầu tiên cùng type (active) làm khuôn variables
      const type = this.form.type;
      this.$bgService.get(this.service, {params: {type, isActive: 'true', limit: 50}})
        .then(res => {
          const candidates = (res.data.docs || []).filter(t => t._id !== this.form._id);
          if (!candidates.length) {
            this.$swal({title: this.$t('error'), text: this.$t('noCloneSource'), icon: 'info'});
            return;
          }
          // Ưu tiên isSystem
          candidates.sort((a, b) => Number(!!b.isSystem) - Number(!!a.isSystem));
          const src = candidates[0];
          this.$swal({
            title: this.$t('cloneConfirmTitle'),
            text: this.$t('cloneConfirmText', [src.name, (src.variables || []).length]),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: this.$t('clone'),
            cancelButtonText: this.$t('cancel'),
          }).then(r => {
            if (!r.value) return;
            this.form.variables = JSON.parse(JSON.stringify(src.variables || []));
          });
        });
    },
    validate() {
      const errs = [];
      if (!/^[a-z][a-z0-9-]{1,49}$/.test(this.form.code || '')) errs.push(this.$t('errCode'));
      if (!this.form.name) errs.push(this.$t('errName'));
      if (!['personal', 'enterprise', 'all'].includes(this.form.type)) errs.push(this.$t('errType'));
      if (!this.isEdit && !this.form.file) errs.push(this.$t('errFile'));
      const seen = new Set();
      for (const v of this.form.variables) {
        if (!/^[a-z][a-z0-9_]*$/.test(v.key || '')) {
          errs.push(this.$t('errVarKey', [v.key || '?']));
          continue;
        }
        if (seen.has(v.key)) errs.push(this.$t('errVarDup', [v.key]));
        seen.add(v.key);
        if (!v.label) errs.push(this.$t('errVarLabel', [v.key]));
        if (!v.source) errs.push(this.$t('errVarSource', [v.key]));
      }
      return errs;
    },
    save() {
      const errs = this.validate();
      if (errs.length) {
        this.$swal({title: this.$t('error'), html: errs.map(e => '• ' + e).join('<br>'), icon: 'error'});
        return;
      }
      this.saving = true;
      const fd = new FormData();
      fd.append('code', this.form.code);
      fd.append('name', this.form.name);
      fd.append('description', this.form.description || '');
      fd.append('type', this.form.type);
      fd.append('variables', JSON.stringify(this.form.variables));
      fd.append('isActive', this.form.isActive ? 'true' : 'false');
      if (this.form.file) fd.append('file', this.form.file);

      const req = this.isEdit
        ? this.$service.put(this.service + '/' + this.form._id, fd)
        : this.$service.post(this.service, fd);

      req.then(() => {
        this.$swal({title: this.$t('success'), icon: 'success', timer: 1500});
        this.show = false;
        this.$emit('saved');
      }).catch(err => {
        this.$swal({title: this.$t('error'), text: (err.response && err.response.data && err.response.data.message) || err.message, icon: 'error'});
      }).finally(() => { this.saving = false; });
    },
  },
  i18n: {
    messages: {
      vi: {
        loading: 'Đang tải...',
        createTitle: 'Thêm mẫu hợp đồng KYC',
        editTitle: 'Sửa mẫu hợp đồng KYC',
        basicInfo: 'Thông tin cơ bản',
        code: 'Mã (code)',
        codeHelp: 'kebab-case, viết thường, không đổi sau khi tạo. VD: enterprise-standard',
        name: 'Tên hiển thị',
        namePlaceholder: 'VD: Hợp đồng doanh nghiệp - Chuẩn',
        type: 'Loại',
        description: 'Mô tả',
        descriptionPlaceholder: 'Mô tả khi nào dùng template này',
        file: 'File mẫu (.docx)',
        chooseFile: 'Chọn file .docx',
        dropFile: 'Thả file vào đây',
        fileNote: 'File Word có placeholder dạng',
        currentFile: 'File hiện tại',
        uploadNewToReplace: 'upload file mới để thay thế',
        extractPlaceholders: 'Trích biến từ file',
        extracting: 'Đang trích...',
        placeholdersFound: 'Tìm thấy {0} biến trong file',
        missingVars: 'Biến có trong file nhưng chưa cấu hình',
        extraVars: 'Biến cấu hình nhưng không có trong file',
        variables: 'Danh sách biến',
        addVariable: 'Thêm biến',
        cloneFromTemplate: 'Sao chép từ template khác',
        cloneFromTemplateHelp: 'Lấy danh sách biến từ template cùng loại đã có',
        noVariables: 'Chưa có biến nào — bấm "Thêm biến" hoặc "Sao chép từ template khác"',
        varKey: 'key',
        varLabel: 'Nhãn hiển thị',
        varLabelPlaceholder: 'VD: Tên công ty',
        varSource: 'Nguồn (kyc./client./system./manual)',
        varType: 'Kiểu',
        varRequired: 'Bắt buộc',
        varHidden: 'Ẩn',
        remove: 'Xoá',
        cancel: 'Huỷ',
        save: 'Lưu',
        saving: 'Đang lưu...',
        personal: 'Cá nhân',
        enterprise: 'Doanh nghiệp',
        all: 'Cả hai',
        cloneConfirmTitle: 'Sao chép biến',
        cloneConfirmText: 'Sao chép {1} biến từ "{0}"? Danh sách biến hiện tại sẽ bị ghi đè.',
        clone: 'Sao chép',
        noCloneSource: 'Không có template cùng loại để sao chép',
        errCode: 'Mã không hợp lệ (kebab-case, 2-50 ký tự)',
        errName: 'Thiếu tên',
        errType: 'Thiếu loại',
        errFile: 'Thiếu file .docx',
        errVarKey: 'Biến "{0}": key sai định dạng (snake_case)',
        errVarDup: 'Biến "{0}": key trùng',
        errVarLabel: 'Biến "{0}": thiếu nhãn',
        errVarSource: 'Biến "{0}": thiếu nguồn',
        success: 'Thành công',
        error: 'Lỗi',
      },
      en: {
        loading: 'Loading...',
        createTitle: 'New KYC contract template',
        editTitle: 'Edit KYC contract template',
        basicInfo: 'Basic info',
        code: 'Code',
        codeHelp: 'kebab-case, lowercase, immutable after create. E.g. enterprise-standard',
        name: 'Display name',
        namePlaceholder: 'E.g. Standard enterprise contract',
        type: 'Type',
        description: 'Description',
        descriptionPlaceholder: 'When to use this template',
        file: 'Template file (.docx)',
        chooseFile: 'Choose .docx file',
        dropFile: 'Drop file here',
        fileNote: 'Word file with placeholders like',
        currentFile: 'Current file',
        uploadNewToReplace: 'upload new to replace',
        extractPlaceholders: 'Extract placeholders',
        extracting: 'Extracting...',
        placeholdersFound: 'Found {0} placeholders in file',
        missingVars: 'In file but not configured',
        extraVars: 'Configured but not in file',
        variables: 'Variables',
        addVariable: 'Add variable',
        cloneFromTemplate: 'Clone from existing',
        cloneFromTemplateHelp: 'Copy variables from an existing template of same type',
        noVariables: 'No variables — click "Add" or "Clone from existing"',
        varKey: 'key',
        varLabel: 'Label',
        varLabelPlaceholder: 'E.g. Company name',
        varSource: 'Source (kyc./client./system./manual)',
        varType: 'Type',
        varRequired: 'Required',
        varHidden: 'Hidden',
        remove: 'Remove',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving...',
        personal: 'Personal',
        enterprise: 'Enterprise',
        all: 'Both',
        cloneConfirmTitle: 'Clone variables',
        cloneConfirmText: 'Clone {1} variables from "{0}"? Current list will be overwritten.',
        clone: 'Clone',
        noCloneSource: 'No template of same type to clone from',
        errCode: 'Invalid code (kebab-case, 2-50 chars)',
        errName: 'Missing name',
        errType: 'Missing type',
        errFile: 'Missing .docx file',
        errVarKey: 'Variable "{0}": invalid key (snake_case)',
        errVarDup: 'Variable "{0}": duplicated key',
        errVarLabel: 'Variable "{0}": missing label',
        errVarSource: 'Variable "{0}": missing source',
        success: 'Success',
        error: 'Error',
      },
    },
  },
};
</script>
